import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { cn } from '@workspace/ui/lib/utils';

import { PriceText } from '~/components/Price/PriceText';
import { usePriceFormatter } from '~/hooks/usePriceFormatter';
import type { ProductViewProps } from '~/pages/Product/Product';
import { convertPenceToPounds } from '~/utils/formaters';

type Props = Pick<
  ProductViewProps,
  'isMultiBuy' | 'currentPrice' | 'currentItem'
> & {
  isLoading?: boolean;
  /** Gap between the promo and regular prices: wider in the page column than in the buy panel. */
  variant?: 'panel' | 'inline';
};

const PRICE_CLASS =
  'text-[24px] font-bold leading-[30px] tracking-[-0.02em] text-[#141415]';

/**
 * Price line of the DubaiExpress product page: the mix-and-match price with
 * its threshold next to the regular price when the offer is in a promotion,
 * otherwise the current price with the crossed-out old one.
 */
const ProductDEPriceHeadlineView = ({
  isMultiBuy,
  currentPrice,
  currentItem,
  isLoading,
  variant = 'panel',
}: Props) => {
  const { t } = useTranslation();
  const formatPrice = usePriceFormatter();

  if (isLoading) {
    return <Skeleton width={150} height={30} />;
  }

  const promo = currentItem?.mixAndMatch ?? null;
  const oldPrice =
    !isMultiBuy && currentItem?.discountPrice
      ? currentItem.priceFormatted
      : null;

  if (promo) {
    return (
      <div
        className={cn(
          'flex flex-wrap items-start',
          variant === 'inline' ? 'gap-6' : 'gap-4',
        )}
      >
        <span className="flex flex-col">
          <span className={cn(PRICE_CLASS, 'text-[#E94E1A]')}>
            <PriceText>
              {formatPrice(convertPenceToPounds(promo.price))}
            </PriceText>
          </span>
          <span className="text-[#E94E1A] text-[12px] leading-4">
            {t('phrases:mixAndMatchFromItems', { count: promo.threshold })}
          </span>
        </span>
        <span className="flex flex-col">
          <span className={PRICE_CLASS}>
            <PriceText>
              {formatPrice(convertPenceToPounds(promo.regularPrice))}
            </PriceText>
          </span>
          <span className="text-[#8A8A8A] text-[12px] leading-4">
            {t('phrases:mixAndMatchRegularPrice')}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={PRICE_CLASS}>
        <PriceText>{formatPrice(currentPrice ?? 0)}</PriceText>
      </span>
      {oldPrice && (
        <span className="text-[#A0A0A0] text-[14px] leading-5 line-through">
          <PriceText>{formatPrice(oldPrice)}</PriceText>
        </span>
      )}
    </div>
  );
};

export const ProductDEPriceHeadline = observer(ProductDEPriceHeadlineView);
